import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { V3_BASE_URL } from '@appmaker/core/api/core';
import { useMemo } from 'react';

const P_ANALYTICS_BASE_PATH = '/p-analytics';

// Optimized fetch function with better error handling and request cancellation support
const fetchAnalyticsData = async (projectId, endpoint, params, signal) => {
    if (!projectId) {
        return Promise.reject(new Error("Project ID is required"));
    }
    
    const url = `${V3_BASE_URL}/projects/${projectId}${P_ANALYTICS_BASE_PATH}${endpoint}`;
    
    try {
        const response = await axios.get(url, {
            params,
            withCredentials: true,
            signal, // Support for request cancellation
            timeout: 30000, // 30 second timeout
        });
        return response.data;
    } catch (error) {
        // Don't log if request was cancelled
        if (error.name !== 'CanceledError') {
            console.error(`Error fetching data from ${url}:`, error);
        }
        throw error;
    }
};

// Memoized query key factory to prevent unnecessary re-renders
const createQueryKey = (endpoint, projectId, params) => {
    return [endpoint, projectId, JSON.stringify(params)];
};

/**
 * Optimized hook for fetching general analytics data.
 * @param {string} projectId - The ID of the project.
 * @param {object} queryParams - Query parameters with start_date and end_date.
 * @param {object} options - React Query options.
 */
export const useAnalyticsData = (projectId, queryParams = {}, options = {}) => {
    const params = useMemo(() => ({ ...queryParams }), [queryParams]);
    const queryKey = useMemo(() => createQueryKey('analyticsData', projectId, params), [projectId, params]);
    
    return useQuery(
        queryKey,
        ({ signal }) => fetchAnalyticsData(projectId, '/', params, signal).then(res => res.data),
        {
            enabled: !!projectId && !!params.start_date && !!params.end_date,
            staleTime: 5 * 60 * 1000, // 5 minutes
            cacheTime: 10 * 60 * 1000, // 10 minutes
            refetchOnWindowFocus: false,
            retry: 2,
            ...options
        }
    );
};

/**
 * Optimized hook for fetching user funnel analytics data.
 * @param {string} projectId - The ID of the project.
 * @param {object} queryParams - Query parameters with start_date and end_date.
 * @param {object} options - React Query options.
 */
export const useUserFunnelData = (projectId, queryParams = {}, options = {}) => {
    const params = useMemo(() => ({ ...queryParams }), [queryParams]);
    const queryKey = useMemo(() => createQueryKey('userFunnelData', projectId, params), [projectId, params]);
    
    return useQuery(
        queryKey,
        ({ signal }) => fetchAnalyticsData(projectId, '/user-funnel', params, signal).then(res => res.data),
        {
            enabled: !!projectId && !!params.start_date && !!params.end_date,
            staleTime: 5 * 60 * 1000, // 5 minutes
            cacheTime: 10 * 60 * 1000, // 10 minutes
            refetchOnWindowFocus: false,
            retry: 2,
            ...options
        }
    );
};

/**
 * Optimized hook for fetching data related to subscribers.
 * @param {string} projectId - The ID of the project.
 * @param {object} queryParams - Query parameters with start_date and end_date.
 * @param {object} options - React Query options.
 */
export const useSubscribersData = (projectId, queryParams = {}, options = {}) => {
    const params = useMemo(() => ({ ...queryParams }), [queryParams]);
    const queryKey = useMemo(() => createQueryKey('subscribersData', projectId, params), [projectId, params]);
    
    return useQuery(
        queryKey,
        ({ signal }) => fetchAnalyticsData(projectId, '/subscribers', params, signal).then(res => res.data),
        {
            enabled: !!projectId && !!params.start_date && !!params.end_date,
            staleTime: 5 * 60 * 1000, // 5 minutes
            cacheTime: 10 * 60 * 1000, // 10 minutes
            refetchOnWindowFocus: false,
            retry: 2,
            ...options
        }
    );
};

/**
 * Optimized hook for fetching analytics data for users segmented by platform.
 * @param {string} projectId - The ID of the project.
 * @param {object} queryParams - Query parameters with start_date and end_date.
 * @param {object} options - React Query options.
 */
export const useUserByPlatformData = (projectId, queryParams = {}, options = {}) => {
    const params = useMemo(() => ({ ...queryParams }), [queryParams]);
    const queryKey = useMemo(() => createQueryKey('userByPlatformData', projectId, params), [projectId, params]);
    
    return useQuery(
        queryKey,
        ({ signal }) => fetchAnalyticsData(projectId, '/user-by-platform', params, signal).then(res => res.data),
        {
            enabled: !!projectId && !!params.start_date && !!params.end_date,
            staleTime: 5 * 60 * 1000, // 5 minutes
            cacheTime: 10 * 60 * 1000, // 10 minutes
            refetchOnWindowFocus: false,
            retry: 2,
            ...options
        }
    );
};

/**
 * Optimized mutation for downloading a total sales report.
 * Uses useMutation as this is an action resulting in a download.
 */
export const useDownloadTotalSalesReport = (options = {}) => {
    return useMutation(
        async ({ projectId, queryParams, signal }) => {
            const params = { 
                start_date: queryParams.start_date,
                end_date: queryParams.end_date,
                store_url: queryParams.store_url, 
            };
            return fetchAnalyticsData(projectId, '/download-total-sales', params, signal);
        },
        {
            retry: 1, // Only retry once for downloads
            ...options
        }
    );
};

/**
 * Optimized mutation for downloading an order report.
 * Uses useMutation as this is an action resulting in a download.
 * Note: end_date and store_url are not used by this endpoint.
 */
export const useDownloadOrderReport = (options = {}) => {
    return useMutation(
        async ({ projectId, queryParams, signal }) => {
            const params = {
                start_date: queryParams.start_date
            };
            return fetchAnalyticsData(projectId, '/download-order-report', params, signal);
        },
        {
            retry: 1, // Only retry once for downloads
            ...options
        }
    );
}; 